import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { getPermissions, ROLE_SECTIONS } from '../utils/permissions';

const AuthContext = createContext(null);

const normalizeStaffUser = (raw) => {
  if (!raw || typeof raw !== 'object') return raw;
  const user = { ...raw };
  // Legacy sessions: role without type → staff
  if (!user.type && user.role && ROLE_SECTIONS[user.role]) {
    user.type = 'staff';
  }
  return user;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem('lenmo_token');
    const storedUser = localStorage.getItem('lenmo_user');
    if (storedToken && storedUser) {
      try {
        const normalized = normalizeStaffUser(JSON.parse(storedUser));
        setToken(storedToken);
        setUser(normalized);
        localStorage.setItem('lenmo_user', JSON.stringify(normalized));
      } catch {
        localStorage.removeItem('lenmo_token');
        localStorage.removeItem('lenmo_user');
      }
    }
    setLoading(false);
  }, []);

  const login = useCallback((tokenData, userData) => {
    const normalized = normalizeStaffUser({ ...userData, type: userData.type || 'staff' });
    localStorage.setItem('lenmo_token', tokenData);
    localStorage.setItem('lenmo_user', JSON.stringify(normalized));
    setToken(tokenData);
    setUser(normalized);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('lenmo_token');
    localStorage.removeItem('lenmo_user');
    setToken(null);
    setUser(null);
  }, []);

  // Member portal users must not get staff permissions; missing type + staff role = staff
  const permissions = useMemo(() => {
    if (!user) return getPermissions(null);
    if (user.type === 'member') return getPermissions(null);
    return getPermissions(user.role || null);
  }, [user]);

  const isStaff = user?.type === 'staff' || (!!user?.role && user?.type !== 'member' && !!ROLE_SECTIONS[user?.role]);
  const isMember = user?.type === 'member';
  const isAdmin = isStaff && user?.role === 'admin';
  const isSupervisor = isStaff && user?.role === 'supervisor';
  const isMarketing = isStaff && user?.role === 'marketing';
  const isAccountant = isStaff && user?.role === 'accountant';
  const isViewer = isStaff && user?.role === 'viewer';
  const isSystemAdmin = isAdmin;
  const canMutate = isStaff && permissions.canMutate;

  return (
    <AuthContext.Provider value={{
      user, token, loading, login, logout,
      permissions,
      isAdmin, isSupervisor, isMarketing, isAccountant, isViewer,
      isStaff, isMember, isSystemAdmin, canMutate,
      canAccess: permissions.canAccess,
      canMenu: permissions.canMenu || permissions.canAccess,
      canWrite: permissions.canWrite,
      canDelete: permissions.canDelete,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export default AuthContext;
